import React, { useState } from "react"
import styled, { keyframes } from "styled-components"
import { MobergFontFamily } from "../MobergFont/MobergFont"
import { canOpenOriginal, useOpenOriginal } from "./originalPdf"

// The sources behind a document-backed answer.
//
// This is the component that makes Clara worth using over a general assistant. Copilot
// told a nurse five times that it did not have her hospital's policy; our answer is only
// better if she can see exactly where it came from and go and check it. A citation she
// cannot verify is worth less than none, because it looks authoritative.
//
// Each card is a citation: the document, its type and page, and the verified quote the
// answer rests on. The whole passage stays one click away, never gone, and never in
// the way.

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`

const fadeUp = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to   { opacity: 1; transform: translateY(0); }
`

// The site's own classification from the upload menu (Validated Policy, Guideline...).
// Shown so a reader can see at a glance what kind of authority stands behind a quote;
// absent entirely for documents the site has not classified.
function locationOf(source) {
	const parts = []
	if (source.section) parts.push(source.section)
	if (source.page_start) parts.push(`page ${source.page_start}`)
	return parts.join(" · ")
}

export function ContextCards({ sources = [], label = "Sources" }) {
	// Passages collapsed by default. The answer and its quote are what the clinician
	// came for; the passage is there to be checked when she wants to check it. One at a
	// time, so opening a second closes the first and the chat never grows unexpectedly
	// under the reader.
	const [openId, setOpenId] = useState(null)
	const openOriginal = useOpenOriginal()

	if (!sources.length) return null

	return (
		<Wrap>
			<Head>
				<HeadLabel>{label}</HeadLabel>
				<Count>{sources.length}</Count>
			</Head>

			{sources.map((source, index) => {
				const id = source.chunk_id ?? index
				const open = openId === id
				const original = canOpenOriginal(source)
				const linked = original || Boolean(source.source_url)
				const where = locationOf(source)
				const quotes = source.quotes || []
				return (
					<Card key={id} $delay={index * 90}>
						<CardHead>
							<DocIcon viewBox="0 0 24 24" aria-hidden>
								<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
								<path d="M14 3v5h5" />
							</DocIcon>
							<TitleText title={source.document_title}>{source.document_title}</TitleText>
							{source.doc_number && <DocNumber>{source.doc_number}</DocNumber>}
							{source.review_status === "overdue" && (
								<OverdueTag>
									{source.next_review_date ? `Review overdue · due ${reviewDate(source.next_review_date)}` : "Review overdue"}
								</OverdueTag>
							)}
						</CardHead>

						{quotes.length > 0 && (
							<Quote>
								{quotes.map(quote => (
									<p key={quote}>“{quote}”</p>
								))}
							</Quote>
						)}

						<CardFoot>
							<PassageToggle type="button" aria-expanded={open} onClick={() => setOpenId(open ? null : id)}>
								{open ? "Hide the passage" : "Show the full passage"}
								<Chevron viewBox="0 0 24 24" $open={open} aria-hidden>
									<path d="M6 9l6 6 6-6" />
								</Chevron>
							</PassageToggle>
							<FootEnd>
								{source.document_type && <TypeTag>{source.document_type}</TypeTag>}
								{(where || linked) && (
									<Where
										as={original ? "button" : source.source_url ? "a" : "span"}
										type={original ? "button" : undefined}
										onClick={original ? () => openOriginal(source.document_id, source.page_start) : undefined}
										href={!original && source.source_url ? source.source_url : undefined}
										target={!original && source.source_url ? "_blank" : undefined}
										rel={!original && source.source_url ? "noreferrer" : undefined}
										title={original ? (source.page_start ? `Open the PDF at page ${source.page_start}` : "Open the PDF") : linked ? "Open the source" : undefined}
										$link={linked}
									>
										{where || "Open"}
										{linked && (
											<Arrow viewBox="0 0 24 24" aria-hidden>
												<path d="M7 17L17 7M7 7h10v10" />
											</Arrow>
										)}
									</Where>
								)}
							</FootEnd>
						</CardFoot>
						<Collapse $open={open}>
							<CollapseInner>
								<Passage>{source.text}</Passage>
							</CollapseInner>
						</Collapse>
					</Card>
				)
			})}
		</Wrap>
	)
}

const Wrap = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 12px;
	min-width: 0;
`

const Head = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 2px;
	animation: ${fadeIn} 400ms ease-out both;
`

const HeadLabel = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: 600;
	color: var(--text-color);
`

const Count = styled.span`
	display: inline-flex;
	align-items: center;
	height: 19px;
	padding: 0 7px;
	border-radius: 6px;
	background: var(--color-hover-neutral);
	border: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11.5px;
	font-weight: 500;
	font-variant-numeric: tabular-nums;
	color: var(--text-secondary-color);
`

const Card = styled.div`
	overflow: hidden;
	padding: 12px 16px 10px;
	border-radius: 12px;
	background: var(--color-surface-panel);
	border: 1px solid var(--color-border-light);
	animation: ${fadeUp} 400ms cubic-bezier(0.23, 1, 0.32, 1) ${({ $delay }) => $delay}ms both;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

// Wraps rather than clips, so a long title keeps its number and tags.
const CardHead = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 8px;
	min-width: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	font-weight: 500;
	color: var(--text-color);
`

const DocIcon = styled.svg`
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	fill: none;
	stroke: var(--color-accent-interactive);
	stroke-width: 1.8;
	stroke-linecap: round;
	stroke-linejoin: round;
`

const TitleText = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const DocNumber = styled.span`
	flex-shrink: 0;
	font-size: 12px;
	font-weight: 400;
	color: var(--text-secondary-color);
`

// The passage toggle on the left; the document type and the cited page on the right.
const CardFoot = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 8px;
	margin-top: 8px;
`

const FootEnd = styled.span`
	display: inline-flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	align-items: center;
	gap: 6px 8px;
	min-width: 0;
	margin-left: auto;
`

// The cited page. A link to the source when the document has one.
const Where = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
	padding: 2px 6px;
	border-radius: 6px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 400;
	color: ${({ $link }) => ($link ? "var(--color-accent-interactive)" : "var(--text-secondary-color)")};
	font-variant-numeric: tabular-nums;
	text-decoration: none;
	border: 0;
	background: transparent;
	cursor: ${({ $link }) => ($link ? "pointer" : "inherit")};
	transition: background-color 130ms ease;

	&:hover {
		background: ${({ $link }) => ($link ? "var(--color-hover-neutral)" : "transparent")};
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 1px;
	}
`

// The verified words, set apart by a subtle background so they read as the document's voice.
const Quote = styled.blockquote`
	&,
	& * {
		-webkit-user-select: text;
		user-select: text;
	}
	margin: 10px 0 0;
	padding: 10px 14px;
	border-radius: 8px;
	background: var(--color-hover-neutral);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	font-style: italic;
	line-height: 1.6;
	color: var(--text-color);

	p {
		margin: 0;
	}

	p + p {
		margin-top: 6px;
	}
`

const PassageToggle = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	margin-left: -6px;
	padding: 3px 6px;
	background: none;
	border: none;
	border-radius: 6px;
	cursor: pointer;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 500;
	color: var(--text-secondary-color);
	transition: background-color 130ms ease;

	&:hover {
		background: var(--color-hover-neutral);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 1px;
	}
`

// Once opened, the document's own words in full, never truncated. A nurse deciding
// whether to trust an answer needs the whole sentence, not a preview of it.
const Passage = styled.p`
	-webkit-user-select: text;
	user-select: text;
	margin: 0;
	padding: 6px 0 4px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	line-height: 1.6;
	color: var(--text-secondary-color);
`

const TypeTag = styled.span`
	flex-shrink: 0;
	padding: 1px 8px;
	border-radius: 999px;
	background: var(--color-hover-neutral);
	font-size: 10.5px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--text-secondary-color);
	white-space: nowrap;
`

// A source past its review date is flagged on the card itself, next to the warning in the
// answer text (UTSW, Nitya 15 Sep). Filled, darker and slightly larger than the type tag:
// 10px of --color-danger-text on white measured about 2.9:1, under the 4.5:1 a warning
// needs, and the date it was due sat in a tooltip nobody hovers.
const OverdueTag = styled(TypeTag)`
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0;
	text-transform: none;
	border-color: transparent;
	background: var(--color-danger-hover-bg, rgba(229, 115, 115, 0.12));
	color: var(--color-danger-text-hover, #d32f2f);
`

// "2026-03-31" -> "31 Mar 2026", the way Clara's answers write dates.
export function reviewDate(iso) {
	const [year, month, day] = String(iso).slice(0, 10).split("-").map(Number)
	if (!year || !month || !day) return String(iso)
	const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
	return `${day} ${names[month - 1]} ${year}`
}

const Arrow = styled.svg`
	width: 9px;
	height: 9px;
	flex-shrink: 0;
	fill: none;
	stroke: currentColor;
	stroke-width: 2.5;
	stroke-linecap: round;
	stroke-linejoin: round;
`

const Chevron = styled.svg`
	width: 13px;
	height: 13px;
	flex-shrink: 0;
	fill: none;
	stroke: var(--text-secondary-color);
	stroke-width: 2.2;
	stroke-linecap: round;
	stroke-linejoin: round;
	transition: transform 280ms cubic-bezier(0.23, 1, 0.32, 1);
	transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`

// grid-template-rows 0fr -> 1fr animates height without measuring the content, so a
// long passage cannot leave the card stuck at a stale height.
const Collapse = styled.div`
	display: grid;
	grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
	transition: grid-template-rows 320ms cubic-bezier(0.23, 1, 0.32, 1);

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const CollapseInner = styled.div`
	overflow: hidden;
`

export default ContextCards
